import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, RefreshCw, UserCheck, Wrench, MapPin, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Box, Filter, ArrowUpDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { exportToExcel } from './exportUtils'; 
import { usePermissions } from './usePermissions'; 

const LaborList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('labors'); 

  // --- Session Data ---
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  // --- State ---
  const [labors, setLabors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // 🎯 ربط عرض المواقع بالرابط عشان لو عملت (رجوع) يفتكرها
  const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const laborcode = searchParams.get('laborcode') || "";
  const craft_code = searchParams.get('craft_code') || "";
  const status = searchParams.get('status') || "";
  const site_id = searchParams.get('site_id') || "";
  const sortBy = searchParams.get('sortBy') || "laborcode";
  const sortDir = searchParams.get('sortDir') || "desc";

  // --- 1. Fetch Data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const effectiveSite = !showAllSites && !site_id ? sessionSite : site_id;

      const res = await axios.get('/api/labor', {
        params: { 
          page, limit, q, laborcode, craft_code, status, 
          site_id: effectiveSite, 
          org_id: sessionOrg,
          all_sites: showAllSites,
          sortBy, sortDir 
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLabors(res.data.data);
      setTotalCount(res.data.total);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to load Data"));
    } finally {
      setLoading(false);
    }
  }, [page, q, laborcode, craft_code, status, site_id, sortBy, sortDir, t, showAllSites, sessionSite, sessionOrg]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // --- 2. Filter Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  // 🎯 التعديل السحري: حقن الفلاتر المخفية للديتيلز
  const handleRowClick = (code) => {
    const activeFilters = Object.fromEntries(searchParams.entries());
    
    // حساب الموقع الفعلي زي ما بنعمل بالظبط للباك إند
    const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
    
    let navParams = { ...activeFilters, org_id: sessionOrg };
    
    if (effectiveSite) navParams.site_id = effectiveSite;
    else delete navParams.site_id;
    
    if (showAllSites) navParams.all_sites = true;

    const queryParams = new URLSearchParams(navParams).toString();
    navigate(`/labors/${code}${queryParams ? '?' + queryParams : ''}`);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setShowAllSites(false); // نرجع للوضع الافتراضي (موقعي فقط)
  };

  // --- 3. Sidebar Events ---
  useEffect(() => {
      const openAllSites = () => { 
          setShowAllSites(true); 
          updateFilters({ page: 1, all_sites: true }); 
      };
      const viewMySite = () => { 
          setShowAllSites(false); 
          updateFilters({ page: 1, all_sites: false });
      };
      
      const exportData = () => {
          const activeFilters = Object.fromEntries(searchParams.entries());
          const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
          
          let exportParams = { ...activeFilters, org_id: sessionOrg };
          if (effectiveSite) exportParams.site_id = effectiveSite;
          else delete exportParams.site_id;

          const columnsMap = {
              "laborcode": t('labor.fields.laborcode', 'Labor Code'),
              "displayname": t('labor.fields.fullname', 'Full Name'),
              "craft_code": t('labor.fields.craft', 'Craft'),
              "status": t('common.status', 'Status'),
              "site_id": t('common.site', 'Site')
          };
          exportToExcel('labor', exportParams, columnsMap, t);
      };

      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.addEventListener('GLOBAL_EXPORT', exportData);
      
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
          window.removeEventListener('GLOBAL_EXPORT', exportData);
      };
  }, [searchParams, showAllSites, sessionSite, sessionOrg, t]);

  const totalPages = Math.ceil(totalCount / limit) || 1;
  const uniqueCraftsInPage = useMemo(() => [...new Set(labors.map(l => l.craft_code))].length, [labors]);
  
  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase font-black tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* --- Top Toolbar --- */}
      <div className="bg-white p-3 border-b shadow-sm flex justify-between items-center px-6 z-20">
        <div className="flex items-center gap-4">
          <h2 className="text-[15px] text-[#1a3353] flex items-center gap-2 italic">
            <UserCheck size={22} className="text-orange-500"/> 
            {t('labor.list.title', 'Labor Registry')}
            <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold border ml-2 ${showAllSites ? 'bg-red-100 text-red-700 border-red-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
              {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
            </span>
          </h2>
        </div>

        <div className="flex gap-3 items-center">
            <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-600 font-bold transition-colors">
              <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            <button onClick={() => fetchData()} className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : ""} />
            </button>
            {canCreate && (
              <button onClick={() => navigate('/labors/new')} className="bg-[#1a3353] text-white px-8 py-2 rounded flex items-center gap-2 hover:bg-blue-900 transition-all shadow-md">
                <Plus size={14} /> {t('labor.list.new', 'NEW LABOR')}
              </button>
            )}
        </div>
      </div>

      {/* --- Statistics Bar --- */}
      <div className="grid grid-cols-4 gap-4 p-3 px-6 bg-slate-50 border-b shadow-inner z-10">
        <div className="bg-white p-2.5 rounded border flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><UserCheck size={18}/></div>
            <div>
              <p className="text-gray-400 text-[9px] uppercase font-bold tracking-widest">{t('labor.list.total', 'Total Labor')}</p>
              <p className="text-[#1a3353] text-lg font-black">{totalCount}</p>
            </div>
        </div>
        <div className="bg-white p-2.5 rounded border flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="p-2 bg-orange-100 text-orange-600 rounded-lg"><Wrench size={18}/></div>
            <div>
              <p className="text-gray-400 text-[9px] uppercase font-bold tracking-widest">{t('labor.list.unique_crafts', 'Page Unique Crafts')}</p>
              <p className="text-[#1a3353] text-lg font-black">{uniqueCraftsInPage}</p>
            </div>
        </div>
      </div>

      {/* --- Main Table Container --- */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-white rounded-sm border shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#1a3353] text-white text-[10px] tracking-widest sticky top-0 z-10">
              <tr>
                <SortableHeader label={t('labor.fields.laborcode', 'Labor Code')} sortKey="laborcode" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('labor.fields.fullname', 'Full Name')} sortKey="displayname" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('labor.fields.craft', 'Craft / Trade')} sortKey="craft_code" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('common.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('common.site', 'Site')} sortKey="site_id" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
              </tr>
              {/* Filter Row */}
              <tr className="bg-[#f8fafc]">
                <td className="p-1 border-x border-gray-200">
                    <div className={`relative flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Search className={`absolute text-gray-400 ${isRTL ? 'right-2' : 'left-2'}`} size={12} />
                      <input 
                        className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'pr-6' : 'pl-6'}`}
                        placeholder={t('common.search')} value={q} onChange={e => updateFilters({ q: e.target.value, page: 1 })} 
                      />
                    </div>
                </td>
                <td className="p-1 border-x border-gray-200 bg-gray-100/30"></td>
                <td className="p-1 border-x border-gray-200"><FilterInput value={craft_code} onChange={v => updateFilters({ craft_code: v, page: 1 })} center /></td>
                <td className="p-1 border-x border-gray-200">
                  <select className="w-full p-1.5 text-blue-900 font-black outline-none border border-gray-100 bg-white text-center" value={status} onChange={e => updateFilters({ status: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </td>
                <td className="p-1 border-x border-gray-200"><FilterInput value={site_id} onChange={v => updateFilters({ site_id: v, page: 1 })} center /></td>
              </tr>
            </thead>
            
            <tbody className="divide-y divide-gray-100 font-bold text-gray-600">
              {loading ? (
                <tr><td colSpan="5" className="p-20 text-center font-black text-gray-300 animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : labors.length > 0 ? (
                labors.map((labor, i) => (
                  <tr 
                    key={i} 
                    className={`group hover:bg-blue-50/50 cursor-pointer transition-colors ${isRTL ? 'text-right' : 'text-left'}`}
                    onClick={() => handleRowClick(labor.laborcode)}
                  >
                    <td className={`p-3 border-x border-gray-50 font-black text-[#1a3353] group-hover:text-blue-600 group-hover:underline`}>{labor.laborcode}</td>
                    <td className={`p-3 border-x border-gray-50`}>
                      <div className="flex flex-col">
                        <span className="text-gray-800">{labor.displayname || '---'}</span>
                        <span className="text-[9px] text-orange-500 font-mono">ID: {labor.personid || '---'}</span>
                      </div>
                    </td>
                    <td className={`p-3 border-x border-gray-50 text-center`}>
                      <span className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200 shadow-sm text-[9px]">
                        <Wrench size={10} className="text-slate-400"/> {labor.craft_code || '---'}
                      </span>
                    </td>
                    <td className={`p-3 border-x border-gray-50 text-center`}>
                      <span className={`px-2.5 py-0.5 rounded text-[9px] shadow-sm ${ labor.status === 'ACTIVE' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200' }`}>
                        {labor.status || 'INACTIVE'}
                      </span>
                    </td>
                    <td className={`p-3 border-x border-gray-50 text-center font-bold text-[#1a3353]`}>
                        <div className={`flex items-center justify-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}><MapPin size={10} className="text-blue-400"/> {labor.site_id || '---'}</div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-20 text-center text-gray-400 italic uppercase">
                    {t('labor.list.no_records', 'No labor records found matching your search.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Pagination Footer --- */}
      <div className={`bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Box size={14} className="text-blue-400"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-blue-300">{(page-1)*limit+1}-{Math.min(page*limit, totalCount)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-white/20 ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-orange-400"/>
                <span>{t('common.total', 'Total')}: <span className="text-orange-300">{totalCount}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
                    <ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
                    <ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
                <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1">
                    <span className="text-orange-400 text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
                    <ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
                    <ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-blue-800 transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-orange-400' : 'text-white/30'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder, center = false }) => (
  <input 
    className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${center ? 'text-center' : ''}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} 
  />
);

export default LaborList;